import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { AND } from '~/common/constants/openSearch';
import { Nullable } from '~/eds/src/types/Nullable';
import { TicketStageType } from '~/enums/TicketStageType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { SearchQuery } from '~/features/advanced-search/types';
import { api } from '~/redux/api/createApi';
import { SearchItem, SearchResult } from '~/redux/api/methods/evisearch';
import { ColumnSortOrder } from '~/types/Columns';
import {
  ClauseMeta,
  FilterQueryEntity,
  MultiSelectMeta,
  QueryOperatorEntity,
} from '~/types/OpenSearch';
import { PilotId } from '~/types/PilotId';
import { TableTicket } from '~/types/Ticket';
import { Uuid } from '~/types/Uuid';

type QueryType =
  | FilterQueryEntity<string | number, ClauseMeta | MultiSelectMeta>
  | QueryOperatorEntity;

type Value = {
  value: Nullable<string[]>;
};

type UserStructuredValue = {
  id: PilotId;
  name: string;
  email: string;
};

interface UserValue extends Value {
  value_structured: Nullable<UserStructuredValue[]>;
}

type StageStructuredValue = {
  id: Uuid;
  name: TicketStageType;
  isCurrentStage: boolean;
};

interface EnabledStagesValue extends Value {
  value_structured: Nullable<StageStructuredValue[]>;
}

type TicketData = {
  id: Uuid;
  selected_field_values: {
    name_Ticket: Value;
    enabled_stages_Ticket: EnabledStagesValue;
    submitter_Ticket: UserValue;
    submitter_name_Ticket: Value;
    stage_Ticket: Value;
    ticket_cancelled_date_Ticket: Value;
    ticket_completed_date_Ticket: Value;
    ticket_submit_date_Ticket: Value;
    ticket_last_activity_date_Ticket: Value;
    assignee_Ticket: UserValue;
    participant_Ticket: UserValue;
    current_party_type_Ticket: Value;
    turns_count_Ticket: Value;
    current_turn_start_date_Ticket: Value;
  };
};

const getStatus = (
  stage: TicketStageType,
  isCompleted: boolean,
  isCancelled: boolean,
): TicketStatusType => {
  if (isCompleted) {
    return TicketStatusType.Completed;
  }
  if (isCancelled) {
    return TicketStatusType.Cancelled;
  }

  switch (stage) {
    case TicketStageType.Edit:
      return TicketStatusType.Editing;
    case TicketStageType.Review:
      return TicketStatusType.Review;
    case TicketStageType.Sign:
      return TicketStatusType.PendingSignatures;
    case TicketStageType.Finalize:
      return TicketStatusType.PendingFinalization;
    default:
      return TicketStatusType.PendingCompletion;
  }
};

const transformTicketList = (
  ticketList: SearchItem<EnabledStagesValue | UserValue>[],
  intl: IntlShape,
): TableTicket[] => {
  return ticketList.map(({ id, selected_field_values }) => {
    const ticketData = { id, selected_field_values } as unknown as TicketData;
    const currentStage =
      ticketData.selected_field_values?.stage_Ticket?.value?.[0] || '';
    const isCompleted =
      !!ticketData.selected_field_values.ticket_completed_date_Ticket.value;
    const isCancelled =
      !!ticketData.selected_field_values.ticket_cancelled_date_Ticket.value;
    return {
      assignees:
        ticketData.selected_field_values.assignee_Ticket?.value_structured?.map(
          (v: any) => v.id,
        ) || [],
      createdDate: ticketData.selected_field_values.ticket_submit_date_Ticket
        .value?.[0]
        ? new Date(
            ticketData.selected_field_values.ticket_submit_date_Ticket
              .value?.[0],
          )
        : null,
      creatorName:
        ticketData.selected_field_values.submitter_Ticket.value_structured?.[0]
          ?.name || '',
      creator:
        ticketData.selected_field_values.submitter_Ticket.value_structured?.[0]
          ?.id,
      currentParty:
        ticketData.selected_field_values.current_party_type_Ticket
          ?.value?.[0] ||
        intl.formatMessage({
          id: 'useSearchTicketListQuery.currentPartyNotSet',
          defaultMessage: 'Not Set',
          description: 'Default text when the current party is not set',
        }),
      currentStageName: currentStage,
      currentTurnStartDate: ticketData.selected_field_values
        .current_turn_start_date_Ticket?.value?.[0]
        ? new Date(
            ticketData.selected_field_values.current_turn_start_date_Ticket
              .value[0],
          )
        : null,
      enabledStages:
        ticketData.selected_field_values.enabled_stages_Ticket.value || '',
      id,
      isCompleted,
      isCancelled,
      lastActivityDate:
        ticketData.selected_field_values.ticket_last_activity_date_Ticket
          ?.value?.[0] || '',

      name: ticketData.selected_field_values.name_Ticket?.value?.[0],
      participants:
        ticketData.selected_field_values.participant_Ticket.value_structured?.map(
          (v: UserStructuredValue) => v.id,
        ) || [],
      status: getStatus(
        currentStage as TicketStageType,
        isCompleted,
        isCancelled,
      ),
      turnsCount:
        ticketData.selected_field_values.turns_count_Ticket?.value?.[0] || 0,
    };
  }) as TableTicket[];
};

const buildFiltersQuery = (query: SearchQuery): QueryType[] => {
  if (query.length === 0) return [];
  return isActiveQuery(query) as QueryType[];
};

interface SearchTicketListParams {
  params: {
    pageSize: number;
    page?: number;
    sortBy?: ColumnSortOrder;
  };
  searchText?: string;
  query?: SearchQuery;
  options?: {
    skip: boolean;
  };
}

export const useSearchTicketListQuery = ({
  params: {
    page = 1,
    pageSize,
    sortBy = { id: 'ticket_last_activity_date_Ticket', desc: true },
  },
  options,
  searchText,
  query = [],
}: SearchTicketListParams) => {
  const intl = useIntl();

  const filtersQuery = buildFiltersQuery(query);

  const finalQuery = (() => {
    const queryParts: QueryType[][] = [];

    if (filtersQuery.length > 0) {
      queryParts.push(filtersQuery);
    }

    if (searchText) {
      const searchQuery = {
        id: 'search_bar',
        operator: 'simple_text_search',
        type: 'filter',
        value: { query: searchText },
      } as unknown as QueryType;
      queryParts.push([searchQuery]);
    }

    return queryParts.reduce(
      (acc: QueryType[], part: QueryType[], index: number) => {
        if (index > 0) {
          acc.push(AND);
        }
        return acc.concat(part);
      },
      [],
    );
  })();

  const searchParams = {
    page,
    pageSize,
    sortBy: { ...sortBy },
    type: 'ticket' as const,
    query: finalQuery,
    filters: [
      'name_Ticket',
      'stage_Ticket',
      'enabled_stages_Ticket',
      'submitter_Ticket',
      'ticket_submit_date_Ticket',
      'ticket_last_activity_date_Ticket',
      'assignee_Ticket',
      'participant_Ticket',
      'ticket_cancelled_date_Ticket',
      'ticket_completed_date_Ticket',
      'current_party_type_Ticket',
      'turns_count_Ticket',
      'current_turn_start_date_Ticket',
    ],
  };

  // useQuery is not returning the correct type when passing options
  const { data, ...rest } = api.endpoints.search.useQuery(searchParams, {
    skip: options?.skip,
  });

  const dataTyped = data as unknown as SearchResult<
    EnabledStagesValue | UserValue
  >;

  return {
    data: transformTicketList(dataTyped?.results || [], intl),
    count: data?.meta?.total || 0,
    ...rest,
  };
};
